import produce from 'immer';
import { createModel, RematchDispatch } from '@rematch/core';

import baseEntity from './base.entity';
import { storeName as floorEntityStore } from './floor.entity';
import { storeName as childItemEntityStore } from './projectChildItem.entity';
import { storeName as facilityEntityStore } from './facility.entity';
import { RootModel } from '../models';
import {
  ProjectItemTypes,
  SimplifiedFacilityTypes,
  SimplifiedFloorTypes,
  SimplifiedProjectChildItemTypes,
} from '../../types';
import { convertProjectItemResponse } from '../../utils';
import { PROJECT_ITEM_TYPE } from '../../commons/constants';
import { storeName as layoutModelStore } from '../models/layout.model';
import {
  getProjectItemByKey,
  getProjectItemByIds,
  getProjectItemById,
} from '../../services/projectItem.service';
import { ProjectItemResponseTypes } from '../../types/response/projectItem';

export const storeName = 'projectItemEntity';

export const projectItemEntity = createModel<RootModel>()({
  name: storeName,
  state: { ids: {} },
  reducers: {
    ...baseEntity,
    saves: produce((state, entities: ProjectItemTypes[]) => {
      (entities || []).forEach((entity: ProjectItemTypes) => {
        state.ids[entity.id] = entity;
      });
    }),
  },
  effects: (dispatch: RematchDispatch<typeof projectItemEntity>) => ({
    async findByIds(payload: number[], state: RootModel) {
      const { locale } = state[layoutModelStore];
      const entities: ProjectItemTypes[] = state[storeName].ids;
      const savedItems: ProjectItemTypes[] = [];
      const ids: number[] = [];
      payload.forEach((id) => {
        const item = entities[id];
        if (item && !item.isSimplified) {
          savedItems.push(item);
        } else {
          ids.push(id);
        }
      });

      if (!ids.length) return savedItems;

      const floors: SimplifiedFloorTypes[] = [];
      const villas: SimplifiedProjectChildItemTypes[] = [];
      let listFacility: SimplifiedFacilityTypes[] = [];

      const resp: ProjectItemResponseTypes[] = await getProjectItemByIds(ids, locale.apiLanguage);

      const newItems: ProjectItemTypes[] = resp.map((i) => convertProjectItemResponse(i));
      newItems.forEach((item) => {
        const { facilities = [], groupFacilities = [], items = [], type } = item;
        listFacility = [...facilities, ...groupFacilities];

        switch (type) {
          case PROJECT_ITEM_TYPE.BUILDING:
            floors.push(...(items as SimplifiedFloorTypes[]));
            break;

          case PROJECT_ITEM_TYPE.VILLA_BLOCK:
            villas.push(...(items as SimplifiedProjectChildItemTypes[]));
            break;

          default:
            break;
        }
      });

      dispatch[floorEntityStore].saveSimplified(floors);
      dispatch[childItemEntityStore].saveSimplified(villas);
      dispatch[facilityEntityStore].saveSimplified(listFacility);

      this.saves(newItems);

      return [...savedItems, ...newItems];
    },
    async loadByKey(payload: string, state: RootModel) {
      const { locale } = state[layoutModelStore];

      const entities: { [key: string]: ProjectItemTypes } = state[storeName].ids;
      // TODO: NEED UPDATE AFTER CHANGE STORE REDUX BY CODE
      const savedItem: ProjectItemTypes = Object.values(entities).find((i) => i.code === payload);
      if (savedItem && !savedItem.isSimplified) return savedItem;

      const resp = await getProjectItemByKey(payload, locale.apiLanguage);
      if (resp.notFound) return resp;
      const item = convertProjectItemResponse(resp);
      const { items = [], type } = item;
      switch (type) {
        case PROJECT_ITEM_TYPE.BUILDING:
          dispatch[floorEntityStore].saveSimplified([...(items as SimplifiedFloorTypes[])]);
          break;
        case PROJECT_ITEM_TYPE.VILLA_BLOCK:
          dispatch[childItemEntityStore].saveSimplified([
            ...(items as SimplifiedProjectChildItemTypes[]),
          ]);
          break;

        default:
          break;
      }
      dispatch[facilityEntityStore].saveSimplified([...(items as SimplifiedFacilityTypes[])]);

      this.saves([item]);

      return item;
    },
    async findById(payload: number, state: RootModel) {
      const { locale } = state[layoutModelStore];
      const entities: ProjectItemTypes[] = state[storeName].ids;
      const item = entities[payload];
      if (item && !item.isSimplified) {
        return item;
      }

      const resp: ProjectItemResponseTypes = await getProjectItemById(payload, locale.apiLanguage);
      const newItem: ProjectItemTypes = convertProjectItemResponse(resp);
      const { facilities = [], groupFacilities = [], items = [], type } = newItem;
      dispatch[facilityEntityStore].saveSimplified([...facilities, ...groupFacilities]);
      switch (type) {
        case PROJECT_ITEM_TYPE.BUILDING:
          dispatch[floorEntityStore].saveSimplified(items as SimplifiedFloorTypes[]);
          break;

        case PROJECT_ITEM_TYPE.VILLA_BLOCK:
          dispatch[childItemEntityStore].saveSimplified(items as SimplifiedProjectChildItemTypes[]);
          break;

        default:
          break;
      }

      this.saves([newItem]);
      return newItem;
    },
    storeProjectItemTemp(payload: ProjectItemResponseTypes) {
      const item = convertProjectItemResponse(payload);
      const { items = [], type } = item;
      switch (type) {
        case PROJECT_ITEM_TYPE.BUILDING:
          dispatch[floorEntityStore].saveSimplified([...(items as SimplifiedFloorTypes[])]);
          break;
        case PROJECT_ITEM_TYPE.VILLA_BLOCK:
          dispatch[childItemEntityStore].saveSimplified([
            ...(items as SimplifiedProjectChildItemTypes[]),
          ]);
          break;

        default:
          break;
      }
      dispatch[facilityEntityStore].saveSimplified([...(items as SimplifiedFacilityTypes[])]);

      this.saves([item]);

      return item;
    },
  }),
});

export default projectItemEntity;
